import { type FC, useCallback, useMemo, useState } from 'react';

import { Heading } from '@vakantiesnl/components/src/__LEGACY__/atoms/Heading';
import { Typography } from '@vakantiesnl/components/src/atoms/Typography';
import { AccommodationBanner } from '@vakantiesnl/components/src/molecules/AccommodationBanner';
import { AdlinksContainer } from '@vakantiesnl/components/src/molecules/AdlinksContainer';
import { ModalOrDrawer } from '@vakantiesnl/components/src/molecules/ModalOrDrawer/ModalOrDrawer';
import { Reviews } from '@vakantiesnl/components/src/organisms/Reviews';
import {
	AccommodationSections,
	MENU_OFFSET,
	MENU_SCROLL_DURATION,
} from '@vakantiesnl/components/src/utils/accoSections';
import { useBreakpoints } from '@vakantiesnl/components/src/utils/useBreakpoints';
import { useAvailabilityCheckContext } from '@vakantiesnl/services/src/context/useAvailabilityCheckContext';
import type { AdlinkValues } from '@vakantiesnl/services/src/gtm/adlinks';
import { useGetAccommodationByEntityId } from '@vakantiesnl/services/src/hooks/queries';
import { useGetReviews } from '@vakantiesnl/services/src/hooks/queries/useGetReviews';
import { useGetFinalOffer } from '@vakantiesnl/services/src/stores';
import { type MicroCopy, type GTM, type Adlinks as AdlinkTypes, type Search } from '@vakantiesnl/types';
import { scroller } from 'react-scroll';

import { useCustomStyles } from './AccommodationReviews.style';

type Props = {
	total: number;
	microCopies: MicroCopy;
	addReviewUrl: string;
	adlinks?: AdlinkTypes.Adlink[];
};

export const AccommodationReviews: FC<Props> = ({ addReviewUrl, total, microCopies, adlinks }) => {
	const { classes } = useCustomStyles();
	const { accommodation } = useGetAccommodationByEntityId();

	const [isAdlinkPopperOpen, setIsAdlinkPopperOpen] = useState(false);

	const currentOffer = useGetFinalOffer(accommodation);

	const { reviewsData, fetchNextReviewPage } = useGetReviews({ id: accommodation.id });
	const { isDesktop } = useBreakpoints();
	const {
		state: { checking },
	} = useAvailabilityCheckContext();

	const reviews = useMemo(() => reviewsData?.pages.flatMap((page) => page.reviews), [reviewsData?.pages]);

	const adlinksCount = useMemo(() => adlinks?.length, [adlinks?.length]);

	const handleTrackActionClick = useCallback(
		(
			accommodation: Search.FullAccommodationItem,
			label: string,
			atom: string,
			type: 'action' | 'load' = 'action',
			action: GTM.GTMAction = 'click',
			adlinksCount: number | undefined,
			isAccoBannerBookBtn?: boolean,
			isAccoBannerProvidersBtn?: boolean,
		) => {
			import('@vakantiesnl/services/src/gtm/common').then(({ track }) =>
				import('@vakantiesnl/services/src/gtm/accommodationPage').then(({ reviewsCTAClick }) =>
					track(
						reviewsCTAClick(
							accommodation,
							type,
							label,
							atom,
							action,
							adlinksCount,
							isAccoBannerBookBtn,
							isAccoBannerProvidersBtn,
						),
					),
				),
			);
		},
		[],
	);

	const onActionClick = useCallback(
		(
			label: string,
			atom: string,
			type: 'action' | 'load' = 'action',
			action: GTM.GTMAction = 'click',
			shouldOpenModal = false,
			isAccoBannerProvidersBtn?: boolean,
		) =>
			() => {
				handleTrackActionClick(
					accommodation,
					label,
					atom,
					type,
					action,
					adlinksCount,
					false,
					isAccoBannerProvidersBtn,
				);
				shouldOpenModal && setIsAdlinkPopperOpen(true);
			},
		[accommodation, adlinksCount, handleTrackActionClick],
	);

	const handleOnClickBookableCTA = useCallback(
		(label: string, atom: string, isAccoBannerBookBtn: boolean) => () => {
			handleTrackActionClick(accommodation, label, atom, 'action', 'click', adlinksCount, isAccoBannerBookBtn);
			scroller.scrollTo(AccommodationSections.Availability, {
				duration: MENU_SCROLL_DURATION,
				smooth: true,
				offset: MENU_OFFSET,
			});
		},
		[accommodation, adlinksCount, handleTrackActionClick],
	);

	const trackReviewsBlockAdlinks = useCallback(
		(adlinkValues: AdlinkValues) => {
			import('@vakantiesnl/services/src/gtm/common').then(({ track }) =>
				import('@vakantiesnl/services/src/gtm/adlinks').then(({ trackAdlinksReviewsBlock }) =>
					track(trackAdlinksReviewsBlock(adlinkValues, accommodation)),
				),
			);
		},
		[accommodation],
	);

	const onAdlinkPopperClose = useCallback(() => {
		setIsAdlinkPopperOpen(false);
	}, []);

	const onLoadMore = useCallback(() => {
		onActionClick(microCopies['reviews.loadMore'], 'morereviews', 'load', 'view')();
		return fetchNextReviewPage();
	}, [fetchNextReviewPage, microCopies, onActionClick]);

	const renderAccommodationBanner = useCallback(() => {
		return (
			<AccommodationBanner
				accommodation={accommodation}
				hasAdlinks={(adlinksCount ?? 0) > 0}
				onActionClick={onActionClick}
				handleOnClickBookableCTA={!currentOffer ? undefined : handleOnClickBookableCTA}
				disableCTAButton={checking}
			/>
		);
	}, [accommodation, adlinksCount, checking, currentOffer, handleOnClickBookableCTA, onActionClick]);

	if (!reviews) {
		return (
			<Heading className={classes.elementContainer} headingLevel="h2" fontWeight="900">
				{microCopies['accommodation.noresults.title']}
			</Heading>
		);
	}

	return (
		<>
			<Reviews
				type="accommodation"
				accommodation={accommodation}
				reviews={reviews}
				reviewCount={total}
				addReviewUrl={addReviewUrl}
				onLoadMore={onLoadMore}
				AccommodationBanner={renderAccommodationBanner}
			/>
			{isAdlinkPopperOpen && (
				<ModalOrDrawer
					open={isAdlinkPopperOpen}
					title={
						!isDesktop
							? microCopies['adlinks.drawer.heading']
							: microCopies['adlinks.hybridproductpage.heading']
					}
					hasCloseIcon={true}
					onClose={onAdlinkPopperClose}
				>
					<AdlinksContainer
						adlinks={adlinks as AdlinkTypes.Adlink[]}
						accommodationName={accommodation.title}
						toggleVariant="secondary"
						showExpanded={true}
						showDefaultDescription={false}
						descriptionToShow={
							<Typography variant="bodyMd" as="p">
								{microCopies['adlinks.popper.description']}
							</Typography>
						}
						onAdlinkClick={trackReviewsBlockAdlinks}
					/>
				</ModalOrDrawer>
			)}
		</>
	);
};
